import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Gamepad2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

const GAME_NAMES = {
  201: "Supernowa", 501: "Powergame", 601: "Ezugi", 604: "Evolution",
  901: "BetGames", 1006: "QTech", 1007: "EvoPlay", 1009: "RTGSlots",
  1010: "HacksawGaming", 1022: "Spribe", 1015: "Smartsoft", 1016: "Jili",
  1017: "StakeGame", 1021: "AWC", 1026: "RoyalGaming", 1028: "VirtualSports",
  1029: "SportbookAlbert", 1030: "AlbertAviator", 1031: "Xtrade",
  1032: "Detrade", 1033: "Mac88", 1036: "ReelGames",
};

function CasinoBets() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }
    API.get(`/api/casino/bets/${user.id}`)
      .then((res) => setBets(Array.isArray(res.data?.bets) ? res.data.bets : []))
      .catch(() => setBets([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const summary = useMemo(() => {
    const totalBet = bets.reduce((s, b) => s + Number(b.bet_amount || 0), 0);
    const totalWin = bets.reduce((s, b) => s + Number(b.win_amount || 0), 0);
    return { totalBet, totalWin, net: totalWin - totalBet };
  }, [bets]);

  return (
    <div style={page}>
      <header style={hdr}>
        <button type="button" style={backBtn} onClick={() => navigate(-1)}>
          <ChevronLeft size={22} color="#fff" />
        </button>
        <span style={hdrTitle}>Casino Bet History</span>
        <div style={{ width: 38 }} />
      </header>

      {/* Summary row */}
      <div style={summaryRow}>
        <SummaryCard label="Total Bet" value={summary.totalBet} color="#f59e0b" />
        <SummaryCard label="Total Win" value={summary.totalWin} color="#22c55e" />
        <SummaryCard label="Net P&L" value={summary.net} color={summary.net >= 0 ? "#22c55e" : "#ef4444"} />
      </div>

      <div style={listWrap}>
        {loading && <div style={empty}>Loading...</div>}
        {!loading && bets.length === 0 && (
          <div style={empty}>
            <Gamepad2 size={40} color="#475569" />
            <p style={{ color: "#64748b", marginTop: 12 }}>No casino bets yet</p>
          </div>
        )}
        {bets.map((bet) => {
          const won = Number(bet.win_amount || 0) > 0;
          const lost = bet.status === "settled" && Number(bet.win_amount || 0) === 0;
          const pending = bet.status === "placed";
          const cancelled = bet.status === "cancelled";
          const net = Number(bet.win_amount || 0) - Number(bet.bet_amount || 0);
          return (
            <div key={bet.id} style={card}>
              <div style={cardTop}>
                <div>
                  <div style={gameLabel}>{GAME_NAMES[Number(bet.tp_game_id)] || `Game ${bet.tp_game_id}`}</div>
                  <div style={tableLabel}>{bet.table_code || "—"}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ ...statusBadge, background: cancelled ? "#374151" : pending ? "#15803d" : won ? "#14532d" : "#7f1d1d" }}>
                    {cancelled ? "Cancelled" : pending ? "Pending" : won ? "Won" : "Lost"}
                  </div>
                </div>
              </div>

              <div style={amtRow}>
                <AmtItem label="Bet" value={`₹${Number(bet.bet_amount).toFixed(2)}`} />
                <AmtItem label="Win" value={`₹${Number(bet.win_amount || 0).toFixed(2)}`} />
                <AmtItem
                  label="Net"
                  value={(net >= 0 ? "+" : "") + `₹${net.toFixed(2)}`}
                  color={net > 0 ? "#22c55e" : net < 0 ? "#ef4444" : "#94a3b8"}
                />
              </div>

              <div style={roundRow}>
                <span style={roundText}>Round: {bet.round_id || "—"}</span>
                <span style={dateText}>{bet.created_at ? new Date(bet.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
      <BottomNav />
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={summaryCard}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color }}>{(value >= 0 ? "" : "-")}₹{Math.abs(value).toFixed(0)}</div>
    </div>
  );
}

function AmtItem({ label, value, color = "#e2e8f0" }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

const DARK = "#0a0a1a";
const CARD = "#13132a";
const BORDER = "rgba(255,255,255,0.08)";

const page = { minHeight: "100vh", background: DARK, color: "#fff", fontFamily: "Inter, system-ui, sans-serif", paddingBottom: 90 };
const hdr = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", height: 54, background: "#0d0d22", borderBottom: `1px solid ${BORDER}`, position: "sticky", top: 0, zIndex: 50 };
const backBtn = { border: 0, background: "transparent", cursor: "pointer", padding: 4, display: "grid", placeItems: "center" };
const hdrTitle = { fontSize: 16, fontWeight: 900, color: "#fff" };

const summaryRow = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "14px 14px 0" };
const summaryCard = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "12px 10px", textAlign: "center" };

const listWrap = { padding: "12px 14px", display: "grid", gap: 10 };
const empty = { textAlign: "center", padding: "60px 0", color: "#475569", display: "flex", flexDirection: "column", alignItems: "center" };

const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "14px 16px", display: "grid", gap: 10 };
const cardTop = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const gameLabel = { fontSize: 14, fontWeight: 800, color: "#e2e8f0" };
const tableLabel = { fontSize: 11, color: "#64748b", marginTop: 2 };
const statusBadge = { fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999, color: "#fff", display: "inline-block" };
const amtRow = { display: "flex", justifyContent: "space-around", borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: "8px 0" };
const roundRow = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const roundText = { fontSize: 10, color: "#475569" };
const dateText = { fontSize: 10, color: "#475569" };

export default CasinoBets;
